// src/components/HtmlComponent.js
import GateRevealPanel from './GateRevealPanel';
import { getPanelRevealTitle } from '../utils/gatePresentation';
import React, {useEffect, useMemo, useRef, useState} from "react";
import { Link, useParams } from 'react-router-dom';
import {useGateAccess} from "../hooks/useGateAccess";
import ComponentControls from "./ComponentControls";
import ComponentAnalyticsSummary from './ComponentAnalyticsSummary';
import TimestampBadge from "./TimestampBadge";
import { resolveTypography } from '../utils/resolveTypography';
import GateBadge from "./GateBadge";
import DirectPaymentUnlock from "./DirectPaymentUnlock";
import PasswordUnlock from "./PasswordUnlock";
import { buildPanelClassName } from '../utils/panelTheme';

export default function HtmlComponent({
                                          comp,
                                          index,
                                          moveUp,
                                          moveDown,
                                          onEdit,
                                          isEditor = false,
                                          removeComponent,

                                          gateType,
                                          selectedListId,
                                          onGateTypeChange,
                                          onGateListChange,
                                          donationCampaignOptions = [],
                                          donationGate,
                                          onDonationGateChange,
                                          onDirectPaymentGateChange,
                                          onDirectPaymentDraftChange,
                                          extraGates,
                                          gateLogic,
                                          onExtraGatesChange,
                                          onGateLogicChange,

                                          style = {},
                                          onStyleChange,
                                          coverUrl = "",
                                          onCoverChange,
                                          onPanelBackgroundImageChange,
                                          timestampDisplay = 'none',
                                          onTimestampDisplayChange,

                                          // NEW (optional): global typography
                                          pageTypography = null,
                                          ownerPublicKey = '',
                                      }) {
    const ownerPublicKeyStr = ownerPublicKey ? String(ownerPublicKey) : '';
    const normalizeGate = (g) => {
        if (!g || g.type !== "directPayment") return g;
        return { ...g, payToUid: ownerPublicKeyStr || g.payToUid || "" };
    };
    const gate = normalizeGate(comp.gate || {type: "public", listId: ""});
    const gates = useMemo(
        () => [
            gate,
            ...(Array.isArray(comp.extraGates) ? comp.extraGates.map(normalizeGate) : []),
        ],
        [gate, comp.extraGates, ownerPublicKeyStr]
    );
    const [gateRefreshKey, setGateRefreshKey] = useState(0);

    const donationMap = useMemo(() => {
        const m = new Map();
        // expect options like { id, campaignName, currency }
        (donationCampaignOptions || []).forEach(o => {
            if (o?.id) {
                m.set(String(o.id), {
                    campaignName: o.campaignName || '',
                    currency: o.currency || 'SOL',
                    tokenMint: o.tokenMint || ''
                });
            }
        });
        return m;
    }, [donationCampaignOptions]);

    const logic = comp.gateLogic === 'all' ? 'all' : 'any';
    const [hasAccess, isLoading, gateNamesLabel, gateNames, gateAccess] = useGateAccess(
        { gates, logic, donationLookup: donationMap },
        { spaceOwnerUid: ownerPublicKeyStr, isEditor: isEditor, refreshKey: gateRefreshKey, componentId: comp.id }
    );
    const { resetPasswordAccess, resetPasswordError } = gateAccess;
    const { username: routeUsername } = useParams();
    const safeUsername = routeUsername || comp?.muultiName || '';
    const donationGateLinks = useMemo(() => {
        if (!safeUsername) return [];
        return (gates || [])
            .filter(g => g?.type === 'donation' && g?.donationCompId)
            .map(g => ({
                id: String(g.donationCompId),
                name: g.campaignName || 'Donation',
                currency: g.currency || '',
                href: `/${safeUsername}/d/${g.donationCompId}`
            }));
    }, [gates, safeUsername]);

    // Typography (panel overrides > global)
    const typo = resolveTypography(pageTypography, style);
    const basePanelClass = buildPanelClassName({ style });
    const analyticsFooter = (
        <ComponentAnalyticsSummary
            comp={comp}
            ownerPublicKey={ownerPublicKeyStr}
            title="Component activity"
            compact
            footer
        />
    );
    const numToPx = v => (typeof v === 'number' ? `${v}px` : (v || undefined));

    // === Height + Resizing ===
    // Initial height: prefer persisted style.minHeight or style.height; fallback to 220.
    const initial = parseInt(
        (style?.minHeight || style?.height || "").toString().replace("px", ""),
        10
    );
    const [height, setHeight] = useState(Number.isFinite(initial) ? initial : 400);
    const [manual, setManual] = useState(Number.isFinite(initial)); // manual mode if a minHeight was saved
    const [dragging, setDragging] = useState(false);
    const dragStart = useRef({y: 0, h: 0});
    const frameRef = useRef(null);

    // Persist height to panel style when user finishes dragging
    const persistHeight = (px) => {
        const clamped = Math.max(120, Math.min(5000, Math.round(px)));
        onStyleChange?.(index, {...style, minHeight: `${clamped}px`});
    };

    // Resize grip handlers
    const onGripDown = (e) => {
        e.preventDefault();
        setManual(true);
        setDragging(true);
        const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
        dragStart.current = {y: clientY, h: height};
        // attach listeners on window to capture outside the grip
        window.addEventListener("mousemove", onGripMove);
        window.addEventListener("mouseup", onGripUp);
        window.addEventListener("touchmove", onGripMove, {passive: false});
        window.addEventListener("touchend", onGripUp);
    };

    const onGripMove = (e) => {
        if (!dragging) return;
        const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
        const dy = clientY - dragStart.current.y;
        const next = Math.max(120, dragStart.current.h + dy);
        setHeight(next);
        e.preventDefault?.();
    };

    const onGripUp = () => {
        setDragging(false);
        window.removeEventListener("mousemove", onGripMove);
        window.removeEventListener("mouseup", onGripUp);
        window.removeEventListener("touchmove", onGripMove);
        window.removeEventListener("touchend", onGripUp);
        persistHeight(height);
    };

    // Double-click (or double-tap) the grip to reset to auto mode
    const onGripDouble = (e) => {
        e.preventDefault();
        setManual(false);
        // Remove persisted minHeight so auto-resize can take over
        const newStyle = {...style};
        delete newStyle.minHeight;
        delete newStyle.height;
        onStyleChange?.(index, newStyle);
    };

    // === CSP allowing p5 from CDNs (still sandboxed) ===
    const SCRIPT_CDNS = [
        "'unsafe-inline'",
        "https://cdn.jsdelivr.net",
        "https://cdnjs.cloudflare.com",
        "https://unpkg.com",
    ].join(" ");

    const csp = [
        "default-src 'none'",
        `script-src ${SCRIPT_CDNS}`,
        "style-src 'unsafe-inline'",
        "img-src * data:",
        "media-src *",
        "font-src data:",
        "connect-src 'none'",
        "frame-ancestors 'none'",
        "base-uri 'none'",
        "form-action 'none'",
        "block-all-mixed-content",
    ].join("; ");

    // === srcDoc with auto-height script ===
    const srcDoc = useMemo(() => {
        const html = comp.html || "";

        // Child script: post content height to parent (auto height)
        const resizeScript = `
      (function() {
        function send() {
          try {
            var h = Math.max(
              document.body.scrollHeight,
              document.documentElement.scrollHeight
            );
            parent.postMessage({ type: 'muulti-html-resize', id: '${comp.id}', h: h }, '*');
          } catch(_) {}
        }
        var ro = new ResizeObserver(send);
        ro.observe(document.documentElement);
        window.addEventListener('load', send);
        setInterval(send, 600); // belt & suspenders
      })();
    `;

        return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta http-equiv="Content-Security-Policy" content="${csp}">
    <style>
      :root { color-scheme: light dark; }
      html, body { margin:0; padding:0; }
      * { box-sizing: border-box; }
      img, video, audio, canvas { max-width: 100%; height: auto; }
      a { color: inherit; }
      body { font: 16px/1.5 system-ui, -apple-system, Segoe UI, Roboto, sans-serif; padding: 12px; }
    </style>
  </head>
  <body>
    ${html}
    <script>${resizeScript}</script>
  </body>
</html>`;
    }, [comp.id, comp.html]);

    // Parent listens for auto-height messages (ignored when in manual mode)
    useEffect(() => {
        const onMsg = (e) => {
            const d = e?.data;
            if (!d || d.type !== "muulti-html-resize" || d.id !== comp.id) return;
            if (manual) return; // manual override active
            const next = Math.max(120, Math.min(5000, Number(d.h) || 0));
            if (next && Math.abs(next - height) > 1) setHeight(next);
        };
        window.addEventListener("message", onMsg);
        return () => window.removeEventListener("message", onMsg);
    }, [manual, height, comp.id]);

    if (isLoading) {
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={style}>
                <h3
                    style={{
                        fontFamily: typo.headers.fontFamily,
                        fontSize: numToPx(typo.headers.fontSize),
                        fontWeight: typo.headers.fontWeight,
                        color: typo.headers.color,
                        lineHeight: typo.headers.lineHeight
                    }}
                >
                    Checking access for {gateNamesLabel}…
                </h3>
                <TimestampBadge
                    mode={comp.timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                {analyticsFooter}
                <ComponentControls
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={onEdit}
                    removeComponent={removeComponent}
                    gateType={gate.type}
                    selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                />
            </div>
        </GateRevealPanel>
        );
    }

    if (!hasAccess) {
        const directPaymentGate =
            (gates || []).find(g => g?.type === "directPayment" && Number(g?.priceSol || 0) > 0);
        const passwordGate =
            (gates || []).find(g => g?.type === "password" && String(g?.password || '').length > 0);
        const deniedHeading = directPaymentGate ? 'x402 Error' : 'Access denied';
        return (
            <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={buildPanelClassName({ style, extra: "locked" })} style={style}>
                <h1
                    style={{
                        fontFamily: typo.headers.fontFamily,
                        fontSize: numToPx(typo.headers.fontSize),
                        fontWeight: typo.headers.fontWeight,
                        color: typo.headers.color,
                        lineHeight: typo.headers.lineHeight
                    }}
                >
                    {deniedHeading} - {gateNamesLabel}
                    {passwordGate ? (
                        <PasswordUnlock
                            gate={passwordGate}
                            componentId={comp.id}
                            onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                        />
                    ) : null}
                    {directPaymentGate ? (
                        <DirectPaymentUnlock
                            gate={directPaymentGate}
                        componentId={comp.id}
                        componentType={comp.type}
                        ownerPublicKey={ownerPublicKeyStr}
                            onUnlocked={() => setGateRefreshKey((v) => v + 1)}
                        />
                    ) : null}
                    {donationGateLinks.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                            {donationGateLinks.map((d) => (
                                <div key={d.id} style={{ marginTop: 4 }}>
                                    <Link to={d.href} className="detail-page-return-link">
                                        View {d.name}{d.currency ? ` (${d.currency})` : ''} details
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                </h1>
                <TimestampBadge
                    mode={comp.timestampDisplay || 'none'}
                    createdAt={comp.createdAt}
                    updatedAt={comp.updatedAt}
                />
                {analyticsFooter}
                <ComponentControls
                    compId={comp.id}
                    index={index}
                    moveUp={moveUp}
                    moveDown={moveDown}
                    onEdit={onEdit}
                    removeComponent={removeComponent}
                    gateType={gate.type}
                    selectedListId={gate.listId}
                    onGateTypeChange={onGateTypeChange}
                    onGateListChange={onGateListChange}
                    extraGates={extraGates ?? comp.extraGates ?? []}
                    gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                    onExtraGatesChange={onExtraGatesChange}
                    onGateLogicChange={onGateLogicChange}
                    donationCampaignOptions={donationCampaignOptions}
                    donationGate={donationGate ?? comp.gate}
                    onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                    style={style}
                    onStyleChange={onStyleChange}
                    coverUrl={coverUrl}
                    onCoverChange={onCoverChange}
                    onPanelBackgroundImageChange={onPanelBackgroundImageChange}
                />
            </div>
        </GateRevealPanel>
        );
    }

    return (
        <GateRevealPanel hasAccess={hasAccess} isLoading={isLoading} access={gateAccess}
            title={getPanelRevealTitle(comp)} panelStyle={style}>
        <div className={basePanelClass} style={{...style, position: "relative"}}>
            <iframe
                ref={frameRef}
                title={`html-${comp.id}`}
                srcDoc={srcDoc}
                sandbox="allow-forms allow-scripts allow-popups allow-pointer-lock allow-top-navigation-by-user-activation"
                style={{
                    width: "100%",
                    height: `${height}px`,
                    border: "none",
                    display: "block",
                    background: "transparent",
                    overflow: "hidden",
                }}
            />
            <div
                onMouseDown={onGripDown}
                onTouchStart={onGripDown}
                onDoubleClick={onGripDouble}
                style={{
                    position: "absolute",
                    left: "50%",
                    bottom: 6,
                    transform: "translateX(-50%)",
                    width: 64,
                    height: 12,
                    borderRadius: 6,
                    cursor: dragging ? "ns-resize" : "grab",
                    background:
                        "repeating-linear-gradient(90deg, rgba(0,0,0,.25), rgba(0,0,0,.25) 6px, rgba(0,0,0,.1) 6px, rgba(0,0,0,.1) 12px)",
                    opacity: 0.6,
                    userSelect: "none",
                    touchAction: "none",
                }}
                title={manual ? "Drag to resize (double-click to auto-fit)" : "Drag to set custom height"}
            />
            {/* Resize grip (drag to resize, double-click to reset to auto) */}
            {hasAccess && (
                <GateBadge  gateNamesLabel={gateNamesLabel} onResetPasswordAccess={resetPasswordAccess} resetPasswordError={resetPasswordError} />
            )}
            <TimestampBadge
                mode={comp.timestampDisplay || 'none'}
                createdAt={comp.createdAt}
                updatedAt={comp.updatedAt}
            />
            {analyticsFooter}
            <ComponentControls
                compId={comp.id}
                index={index}
                moveUp={moveUp}
                moveDown={moveDown}
                onEdit={onEdit}
                removeComponent={removeComponent}
                gateType={gate.type}
                selectedListId={gate.listId}
                onGateTypeChange={onGateTypeChange}
                onGateListChange={onGateListChange}
                extraGates={extraGates ?? comp.extraGates ?? []}
                gateLogic={gateLogic ?? comp.gateLogic ?? 'any'}
                onExtraGatesChange={onExtraGatesChange}
                onGateLogicChange={onGateLogicChange}
                donationCampaignOptions={donationCampaignOptions}
                donationGate={donationGate ?? comp.gate}
                onDonationGateChange={onDonationGateChange}
                onDirectPaymentGateChange={onDirectPaymentGateChange}
                onDirectPaymentDraftChange={onDirectPaymentDraftChange}
                style={style}
                onStyleChange={onStyleChange}
                coverUrl={coverUrl}
                onCoverChange={onCoverChange}
                onPanelBackgroundImageChange={onPanelBackgroundImageChange}
            />
        </div>
        </GateRevealPanel>
    );
}



